import React from 'react';
import { FDIHeadlinePanel } from '../components/panels/fdi/FDIHeadlinePanel';
import { FDITrendPanel } from '../components/panels/fdi/FDITrendPanel';
import { SourceCountriesPanel } from '../components/panels/fdi/SourceCountriesPanel';
import { SectorBreakdownPanel } from '../components/panels/fdi/SectorBreakdownPanel';
import { TopInvestorsPanel } from '../components/panels/fdi/TopInvestorsPanel';
import { RegionalComparisonPanel } from '../components/panels/fdi/RegionalComparisonPanel';
import { PipelinePanel } from '../components/panels/fdi/PipelinePanel';
import { usePanelManager, PanelConfig } from '../hooks/usePanelManager';
import { MinimizedBar } from '../components/ui/MinimizedBar';

// Panel configuration
const panelConfig: PanelConfig[] = [
  { id: 'headline', title: 'FDI Headline', row: 1, colSpan: 2 },
  { id: 'trend', title: 'FDI Trend', row: 1, colSpan: 2 },
  { id: 'sources', title: 'Source Countries', row: 2 },
  { id: 'sectors', title: 'Sector Breakdown', row: 2 },
  { id: 'investors', title: 'Top Investors', row: 2 },
  { id: 'regional', title: 'Regional Comparison', row: 2 },
  { id: 'pipeline', title: 'Pipeline', row: 3, colSpan: 4 },
];

// Panel component map
const panelComponents: Record<string, React.FC<{ isMinimized?: boolean; onMinimize?: () => void }>> = {
  headline: FDIHeadlinePanel,
  trend: FDITrendPanel,
  sources: SourceCountriesPanel,
  sectors: SectorBreakdownPanel,
  investors: TopInvestorsPanel,
  regional: RegionalComparisonPanel,
  pipeline: PipelinePanel,
};

const FDIView: React.FC = () => {
  const {
    minimizedPanelsList,
    handleMinimize,
    handleRestore,
    getOrderedPanels,
    isPanelMinimized,
    getDragState,
    getDragProps,
  } = usePanelManager(panelConfig);

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <MinimizedBar panels={minimizedPanelsList} onRestore={handleRestore} />

      <div className="flex-1 flex flex-col md:grid md:grid-cols-2 lg:grid-cols-4 md:grid-rows-[1fr_1fr_auto] gap-px p-px bg-terminal-border overflow-y-auto md:overflow-hidden">
        {/* Row 1 - FDI Headline & Trend */}
        {getOrderedPanels(1).map(panel => (
          <div
            key={panel.id}
            {...getDragProps(panel.id)}
            className={`min-h-[200px] sm:min-h-[250px] md:min-h-0 col-span-1 lg:col-span-2 h-full transition-all duration-200 ${
              getDragState(panel.id).isDragging ? 'opacity-50 scale-[0.98]' : ''
            } ${getDragState(panel.id).isDragOver ? 'ring-2 ring-data-amber/50 ring-inset' : ''}`}
          >
            {React.createElement(panelComponents[panel.id], {
              isMinimized: isPanelMinimized(panel.id),
              onMinimize: () => handleMinimize(panel.id),
            })}
          </div>
        ))}

        {/* Row 2 - Source, Sectors, Investors, Regional */}
        {getOrderedPanels(2).map(panel => (
          <div
            key={panel.id}
            {...getDragProps(panel.id)}
            className={`min-h-[200px] sm:min-h-[250px] md:min-h-0 h-full transition-all duration-200 ${
              getDragState(panel.id).isDragging ? 'opacity-50 scale-[0.98]' : ''
            } ${getDragState(panel.id).isDragOver ? 'ring-2 ring-data-amber/50 ring-inset' : ''}`}
          >
            {React.createElement(panelComponents[panel.id], {
              isMinimized: isPanelMinimized(panel.id),
              onMinimize: () => handleMinimize(panel.id),
            })}
          </div>
        ))}

        {/* Row 3 - Pipeline */}
        {getOrderedPanels(3).map(panel => (
          <div
            key={panel.id}
            {...getDragProps(panel.id)}
            className={`min-h-[200px] sm:min-h-[250px] md:min-h-0 col-span-full h-full transition-all duration-200 ${
              getDragState(panel.id).isDragging ? 'opacity-50 scale-[0.98]' : ''
            } ${getDragState(panel.id).isDragOver ? 'ring-2 ring-data-amber/50 ring-inset' : ''}`}
          >
            {React.createElement(panelComponents[panel.id], {
              isMinimized: isPanelMinimized(panel.id),
              onMinimize: () => handleMinimize(panel.id),
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default FDIView;
